import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS, NAV_LINKS } from "../content";
import { Logo } from "./Logo";
import { track } from "../lib/track";

const QuoteButton = ({ className = "", onClick }) => (
  <Link
    to="/contact"
    data-testid="header-quote-button"
    onClick={(e) => { track("cta_click", { label: "header_quote" }); onClick?.(e); }}
    className={`inline-flex items-center justify-center rounded-full bg-brand-teal px-6 py-3 text-sm font-bold text-white shadow-md shadow-teal-900/10 transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-teal-600 ${className}`}
  >
    Get a Free Quote
  </Link>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-[background-color,box-shadow] duration-300 ${
          scrolled ? "border-slate-200 bg-white/90 shadow-sm" : "border-transparent bg-white/70"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
          <Logo />
          <nav className="hidden items-center gap-5 xl:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `text-[13px] font-semibold transition-colors duration-200 hover:text-brand-teal ${
                    isActive ? "text-brand-teal" : "text-slate-600"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a
              href={BUSINESS.phoneHref}
              data-testid="header-phone-link"
              onClick={() => track("click_to_call", { location: "header" })}
              className="hidden items-center gap-2 text-sm font-bold text-brand-ink transition-colors hover:text-brand-teal lg:flex"
            >
              <Phone className="h-4 w-4 shrink-0 text-brand-teal" />
              <span className="whitespace-nowrap">{BUSINESS.phone}</span>
            </a>
            <QuoteButton className="hidden sm:inline-flex" />
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(!open)}
              aria-expanded={open}
              aria-label={open ? "Close menu" : "Open menu"}
              className="rounded-full border border-slate-200 bg-white p-2.5 text-brand-ink xl:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        <AnimatePresence>
          {open && (
            <motion.nav
              data-testid="mobile-menu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden border-t border-slate-100 bg-white xl:hidden"
              aria-label="Mobile"
            >
              <div className="space-y-1 px-6 py-6">
                {NAV_LINKS.map((l, i) => (
                  <motion.div key={l.to} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                    <NavLink
                      to={l.to}
                      className={({ isActive }) =>
                        `block rounded-xl px-4 py-3 font-display text-lg font-bold ${isActive ? "bg-brand-mist text-brand-teal" : "text-brand-ink"}`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </motion.div>
                ))}
                <div className="pt-4">
                  <QuoteButton className="w-full" />
                </div>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>

      <div data-testid="mobile-bottom-bar" className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-2 border-t border-slate-200 bg-white/95 backdrop-blur-xl md:hidden">
        <a
          href={BUSINESS.phoneHref}
          data-testid="mobile-call-button"
          onClick={() => track("click_to_call", { location: "mobile_bar" })}
          className="flex items-center justify-center gap-2 py-4 text-sm font-bold text-brand-ink"
        >
          <Phone className="h-4 w-4 text-brand-teal" /> Call Us
        </a>
        <Link
          to="/contact"
          data-testid="mobile-quote-button"
          onClick={() => track("cta_click", { label: "mobile_bar_quote" })}
          className="flex items-center justify-center bg-brand-teal py-4 text-sm font-bold text-white"
        >
          Get a Free Quote
        </Link>
      </div>
    </>
  );
};
